import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import FamilyHeader from "./FamilyHeader";
import FamilyChecklistDatePickerDialog from "./FamilyChecklistDatePickerDialog";
import FamilyDateNavigator from "./FamilyDateNavigator";
import { getSessionContext } from "../../common/utils/sessionContext";
import { getCheckListOfFamilyMember } from "../../common/api/familyportalApi";
import { IoChevronBack, IoChevronForward, IoListOutline, IoTimeOutline, IoCalendarOutline } from "react-icons/io5";
import { FaCheckCircle, FaBan, FaTimesCircle } from "react-icons/fa";
import * as Sentry from "@sentry/react";

export const formatDateKey = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const getTodayDateKey = () => formatDateKey(new Date());

export const formatDateLabel = (dateKey, { isToday = false } = {}) => {
  if (isToday) {
    return "Today";
  }
  try {
    const [y, m, d] = dateKey.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateKey;
  }
};

const CHECKLIST_SECTIONS = [
  { id: "all", label: "All" },
  { id: "completed", label: "Completed", statLabel: "done" },
  { id: "notAvailable", label: "Not Available", statLabel: "n/a" },
  { id: "declined", label: "Declined", statLabel: "declined" },
  { id: "notDone", label: "Not Done", statLabel: "not done" },
];

const STATUS_SECTION_IDS = ["completed", "notAvailable", "declined", "notDone"];

const formatChecklistTime = (updatedAt) => {
  if (!updatedAt) return "";

  const parts = updatedAt.split(" ");
  return `${parts[1]?.slice(0, 5)} ${parts[2]}`;
};

const normalizeChecklistItem = (item) => ({
  id: item.checklistId,
  task: item.title || "",
  description: item.description || "",
  reason: item.reason || "",
  caregiverName: item.caregiverName || "",
  time: formatChecklistTime(item.updatedAt),
  status: String(item.status || "").trim(),
});

const resolveStatusSection = (status) => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();

  if (normalized === "completed") {
    return "completed";
  }
  if (normalized === "not available") {
    return "notAvailable";
  }
  if (normalized === "declined") {
    return "declined";
  }
  if (normalized === "to-do" || normalized === "todo" || normalized === "not done" || normalized === "not completed") {
    return "notDone";
  }

  return "notDone";
};

const mapChecklistApiResponse = (apiData, selectedDateKey) => {
  const todayKey = getTodayDateKey();
  const isToday = selectedDateKey === todayKey;
  const checklists = Array.isArray(apiData?.checklists) ? apiData.checklists.map(normalizeChecklistItem) : [];

  const grouped = {
    completed: [],
    notAvailable: [],
    declined: [],
    notDone: [],
  };

  checklists.forEach((item) => {
    const sectionId = resolveStatusSection(item.status);
    grouped[sectionId].push({ ...item, sectionId });
  });

  const allItems = STATUS_SECTION_IDS.flatMap((id) => grouped[id]);

  const summary = {
    total: checklists.length,
    completed: grouped.completed.length,
    notAvailable: grouped.notAvailable.length,
    declined: grouped.declined.length,
    notDone: grouped.notDone.length,
  };

  const sections = CHECKLIST_SECTIONS.map(({ id, label }) => {
    if (id === "all") {
      return {
        id,
        label,
        count: allItems.length,
        items: allItems,
      };
    }
    return {
      id,
      label,
      count: grouped[id].length,
      items: grouped[id],
    };
  });

  return {
    summary,
    sections,
    isToday,
    hasData: checklists.length > 0,
    date: apiData?.date || selectedDateKey,
  };
};

const emptyChecklistState = (selectedDateKey) => {
  const todayKey = getTodayDateKey();
  return {
    summary: {
      total: 0,
      completed: 0,
      notAvailable: 0,
      declined: 0,
      notDone: 0,
    },
    sections: CHECKLIST_SECTIONS.map(({ id, label }) => ({
      id,
      label,
      count: 0,
      items: [],
    })),
    isToday: selectedDateKey === todayKey,
    hasData: false,
    date: selectedDateKey,
  };
};

const STATUS_META = {
  all: {
    label: "All",
    icon: IoListOutline,
    badgeClass: "family-checklist-badge--all",
    itemClass: "new-family-checklist-item--all",
    titleIconClass: "",
    filterClass: "tab-family-filter-tab--all",
  },
  completed: {
    label: "Completed",
    icon: FaCheckCircle,
    badgeClass: "family-checklist-badge--completed",
    itemClass: "new-family-checklist-item--completed",
    titleIconClass: "new-family-checklist-status-icon--completed",
    filterClass: "tab-family-filter-tab--completed",
  },
  notAvailable: {
    label: "Not Available",
    icon: FaBan,
    badgeClass: "family-checklist-badge--not-available",
    itemClass: "new-family-checklist-item--not-available",
    titleIconClass: "new-family-checklist-status-icon--not-available",
    filterClass: "tab-family-filter-tab--not-available",
  },
  declined: {
    label: "Declined",
    icon: FaTimesCircle,
    badgeClass: "family-checklist-badge--declined",
    itemClass: "new-family-checklist-item--declined",
    titleIconClass: "new-family-checklist-status-icon--declined",
    filterClass: "tab-family-filter-tab--declined",
  },
  notDone: {
    label: "Not Done",
    icon: IoTimeOutline,
    badgeClass: "family-checklist-badge--not-completed",
    itemClass: "new-family-checklist-item--not-done",
    titleIconClass: "new-family-checklist-status-icon--not-done",
    filterClass: "tab-family-filter-tab--not-done",
  },
};

const formatTimeByCaregiver = (time, caregiverName) => {
  const safeTime = String(time || "").trim();
  const safeName = String(caregiverName || "").trim();
  if (safeTime && safeName) {
    return `${safeTime} · by ${safeName}`;
  }
  if (safeTime) {
    return safeTime;
  }
  if (safeName) {
    return `by ${safeName}`;
  }
  return "";
};

const ChecklistItemRow = ({ item, sectionId, meta }) => {
  const [expanded, setExpanded] = useState(false);
  const itemSectionId = item.sectionId || sectionId;
  const itemMeta = STATUS_META[itemSectionId] || meta;
  const StatusIcon = itemMeta.icon;
  const description = String(item.description || "").trim();
  const reason = String(item.reason || "").trim();
  const timeByCaregiver = formatTimeByCaregiver(item.time, item.caregiverName);
  const showCaregiverMeta = (itemSectionId === "completed" || itemSectionId === "notAvailable" || itemSectionId === "declined") && Boolean(timeByCaregiver);
  const showReason = (itemSectionId === "notAvailable" || itemSectionId === "declined") && Boolean(reason);
  const hasDetails = Boolean(description || showReason);

  useEffect(() => {
    setExpanded(false);
  }, [description, reason, item.id]);

  const toggleExpanded = () => {
    if (!hasDetails) {
      return;
    }
    setExpanded((open) => !open);
  };

  return (
    <li className={`new-family-checklist-item ${itemMeta.itemClass}`}>
      <button type="button" className={`new-family-checklist-item-button${hasDetails ? " is-expandable" : ""}`} onClick={toggleExpanded} aria-expanded={hasDetails ? expanded : undefined} tabIndex={hasDetails ? 0 : -1}>
        {StatusIcon && itemSectionId !== "all" ? (
          <span className={`new-family-checklist-status-icon ${itemMeta.titleIconClass}`} aria-label={itemMeta.label} title={itemMeta.label}>
            <StatusIcon aria-hidden="true" />
          </span>
        ) : null}
        <span className="new-family-checklist-item-body">
          <span className="new-family-checklist-item-title">{item.task}</span>
          {expanded && description ? (
            <span className="new-family-checklist-item-desc">
              <strong>Description:</strong> {description}
            </span>
          ) : null}
          {expanded && showReason ? (
            <span className="new-family-checklist-item-reason">
              <strong>Reason:</strong> {reason}
            </span>
          ) : null}
          {showCaregiverMeta ? <span className="new-family-checklist-item-meta">{timeByCaregiver}</span> : null}
        </span>
        {hasDetails ? (
          <span className={`new-family-checklist-expand${expanded ? " is-open" : ""}`} aria-hidden="true">
            <IoChevronForward />
          </span>
        ) : null}
      </button>
    </li>
  );
};

const FamilyChecklist = () => {
  const navigate = useNavigate();
  const todayKey = getTodayDateKey();
  const [selectedDateKey, setSelectedDateKey] = useState(todayKey);
  const [activeFilter, setActiveFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [checklistData, setChecklistData] = useState(() => emptyChecklistState(todayKey));
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const listScrollRef = useRef(null);

  const { familyClientId, familyMemberClientId, familyClientName } = getSessionContext();
  const clientId = familyClientId || familyMemberClientId;
  const { summary, sections, isToday, hasData } = checklistData;
  const dateLabel = formatDateLabel(selectedDateKey, { isToday });
  const progressPercent = summary.total > 0 ? Math.min(100, Math.round((summary.completed / summary.total) * 100)) : 0;

  const activeSection = sections.find((section) => section.id === activeFilter);
  const activeMeta = activeSection ? STATUS_META[activeSection.id] : null;
  const ActiveSectionIcon = activeMeta?.icon;

  const emptyTabMessage = "No tasks available";

  const resetToToday = () => {
    setSelectedDateKey(todayKey);
  };

  const handleDateKeyChange = (nextDateKey) => {
    if (!nextDateKey) {
      return;
    }
    if (nextDateKey > todayKey) {
      setSelectedDateKey(todayKey);
      return;
    }
    setSelectedDateKey(nextDateKey);
  };

  const openDatePicker = () => {
    setShowCustomDatePicker(true);
  };

  useEffect(() => {
    let cancelled = false;

    const fetchChecklist = async () => {
      if (!clientId) {
        setChecklistData(emptyChecklistState(selectedDateKey));
        setError("Please select a client to view checklist.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      try {
        const result = await getCheckListOfFamilyMember(clientId, selectedDateKey);
        if (cancelled) {
          return;
        }

        if (!result.success) {
          setChecklistData(emptyChecklistState(selectedDateKey));
          setError(result.message || "Failed to load checklist.");
          return;
        }

        const mapped = mapChecklistApiResponse(result.data, selectedDateKey);
        setChecklistData(mapped);
        setActiveFilter("all");
      } catch (error) {
        Sentry.captureException(error);
        if (!cancelled) {
          setChecklistData(emptyChecklistState(selectedDateKey));
          setError("Failed to load checklist.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchChecklist();
    return () => {
      cancelled = true;
    };
  }, [clientId, selectedDateKey]);

  useEffect(() => {
    listScrollRef.current?.scrollTo(0, 0);
  }, [activeFilter, selectedDateKey]);

  return (
    <div className="family-page-layout family-checklist-layout family-checklist-layout--new">
      <FamilyHeader clientName={familyClientName} />
      <Backdrop sx={{ color: "#ffba30", zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={loading}>
        <img src={require("../../assets/btTree.png")} className="loading-img" alt="" />
      </Backdrop>

      <div className="family-page-subheader">
        <div className="family-checklist-topbar">
          <button type="button" className="family-checklist-back" onClick={() => navigate("/family-home")} aria-label="Back to home">
            <IoChevronBack />
          </button>
          <h1 className="family-checklist-title-center">Checklist</h1>
          <span className="family-checklist-topbar-spacer" aria-hidden="true" />
        </div>
        <FamilyDateNavigator selectedDateKey={selectedDateKey} onChange={handleDateKeyChange} maxDateKey={todayKey} onOpenCalendar={openDatePicker} />
      </div>

      <div className="family-checklist-page">
        <div className="family-checklist-toolbar">
          {!isToday ? (
            <div className="family-checklist-date-banner">
              <span>Showing checklist for {dateLabel}</span>
              <button type="button" className="family-checklist-reset-link" onClick={resetToToday}>
                <IoCalendarOutline className="family-checklist-reset-link-icon" aria-hidden="true" />
                Go to Today
              </button>
            </div>
          ) : null}

          {!loading && error ? (
            <div className="family-checklist-empty-date">
              <p>{error}</p>
            </div>
          ) : null}

          {!loading && !error && hasData ? (
            <div className="family-checklist-overview-panel">
              <div className="family-checklist-progress-label family-checklist-progress-label--overview">
                <span>
                  <strong>{summary.completed}</strong> of <strong>{summary.total}</strong> completed
                </span>
                <span>{progressPercent}%</span>
              </div>

              <div className="family-checklist-progress-track family-checklist-progress-track--overview" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progressPercent} aria-label={`${summary.completed} of ${summary.total} completed`}>
                <div className="family-checklist-progress-fill" style={{ width: `${progressPercent}%` }} />
              </div>

              <div className="tab-family-filter-tabs" role="tablist" aria-label="Filter checklist by status">
                {sections.map((section) => {
                  const meta = STATUS_META[section.id];
                  const TabIcon = meta?.icon;
                  const isActive = activeFilter === section.id;
                  const isAll = section.id === "all";

                  return (
                    <button
                      key={section.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      className={`tab-family-filter-tab ${meta?.filterClass || ""}${isActive ? " is-active" : ""}${isAll ? " is-all" : ""}`}
                      onClick={() => setActiveFilter(section.id)}
                      title={section.label}
                      aria-label={`${section.label}, ${section.count}`}>
                      {TabIcon ? <TabIcon className="tab-family-filter-tab-icon" aria-hidden="true" /> : null}
                      {isAll ? (
                        <span className="tab-family-filter-tab-label">
                          All <strong>{section.count}</strong>
                        </span>
                      ) : (
                        <span className="tab-family-filter-tab-count">{section.count}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>

        <div className="family-checklist-body">
          {!loading && !error && hasData ? (
            activeSection && activeMeta && activeSection.count > 0 ? (
              <section className="family-checklist-section family-checklist-section--solo family-checklist-section--new-list">
                <div className="family-checklist-section-header family-checklist-section-header--static">
                  <span className={`family-checklist-badge ${activeMeta.badgeClass}`}>
                    {ActiveSectionIcon && activeSection.id !== "all" ? <ActiveSectionIcon aria-hidden="true" /> : null}
                    {activeSection.label}
                  </span>
                </div>
                <div className="family-checklist-list-scroll" ref={listScrollRef}>
                  <ul className="new-family-checklist-list">
                    {activeSection.items.map((item) => (
                      <ChecklistItemRow key={`${item.sectionId || activeSection.id}-${item.id}`} item={item} sectionId={item.sectionId || activeSection.id} meta={STATUS_META[item.sectionId || activeSection.id] || activeMeta} />
                    ))}
                  </ul>
                </div>
              </section>
            ) : (
              <div className="family-checklist-empty-tab" role="status">
                {emptyTabMessage}
              </div>
            )
          ) : null}

          {!loading && !error && !hasData ? (
            <div className="family-checklist-empty-date">
              <p>No tasks available</p>
            </div>
          ) : null}
        </div>
      </div>

      <FamilyChecklistDatePickerDialog open={showCustomDatePicker} onClose={() => setShowCustomDatePicker(false)} value={selectedDateKey} maxDate={todayKey} onChange={handleDateKeyChange} />
    </div>
  );
};

export default FamilyChecklist;
